import { applySkillEditorOperationReceipt } from "./skill-editor-save-conflicts";
import { reconcileSkillEditorSaveFailure } from "./skill-editor-save-reconciliation";
import {
    clearSkillEditorReviewRequired,
    type SkillEditorSource,
    type SkillEditorState,
} from "./skill-editor-state";
import type {
    SkillEditorDraftSavedPublishRejectedRecovery,
    SkillEditorOperationReceipt,
    SkillEditorPublicationToken,
    SkillEditorPublishAttempt,
    SkillEditorPublishStatusUnknownRecovery,
    SkillEditorPublishUnpublishedElsewhereRecovery,
    SkillEditorSaveOperation,
    SkillEditorSavePlan,
} from "./skill-editor-save";

type PublishOperation = Extract<SkillEditorSaveOperation, { kind: "publish" }>;

type SkillEditorPublishFailureCertainty = "status-unknown" | "rejected";

export type SkillEditorPublicationOutcome =
    | {
          readonly kind: "applied";
          readonly state: SkillEditorState;
          readonly completedOperations: readonly SkillEditorOperationReceipt[];
      }
    | {
          readonly kind: "status-unknown";
          readonly state: SkillEditorState;
          readonly recovery: SkillEditorPublishStatusUnknownRecovery;
      }
    | {
          readonly kind: "draft-saved-publish-rejected";
          readonly state: SkillEditorState;
          readonly recovery: SkillEditorDraftSavedPublishRejectedRecovery;
      }
    | {
          readonly kind: "unpublished-elsewhere";
          readonly state: SkillEditorState;
          readonly recovery: SkillEditorPublishUnpublishedElsewhereRecovery;
      }
    | {
          readonly kind: "rejected";
          readonly state: SkillEditorState;
          readonly error: unknown;
      };

export interface SkillEditorPublishFailureInput {
    readonly state: SkillEditorState;
    readonly attempt: SkillEditorPublishAttempt;
    readonly completedOperations: readonly SkillEditorOperationReceipt[];
    readonly error: unknown;
    readonly responseStatus?: number | null;
    readonly authoritative?: SkillEditorSource | null;
}

export interface SkillEditorPublishRetryWarningAcknowledgment {
    readonly acceptsDuplicateVersionRisk: boolean;
}

export interface SkillEditorPublishOnlyRetryPlan {
    readonly attempt: SkillEditorPublishAttempt;
}

const getSkillEditorPublicationToken = (
    source: SkillEditorSource
): SkillEditorPublicationToken => ({
    claudeSkillId: source.claude_skill_id ?? null,
    publishedAt: source.published_at ?? null,
});

export const skillEditorPublicationTokensEqual = (
    left: SkillEditorPublicationToken,
    right: SkillEditorPublicationToken
): boolean =>
    left.claudeSkillId === right.claudeSkillId &&
    left.publishedAt === right.publishedAt;

export function captureSkillEditorPublishAttempt(
    plan: SkillEditorSavePlan
): SkillEditorPublishAttempt | null {
    const operation = plan.operations.find(
        (candidate): candidate is PublishOperation =>
            candidate.kind === "publish"
    );
    if (!operation) return null;
    return {
        operation: { ...operation },
        publicationToken: { ...plan.publicationToken },
    };
}

export function classifySkillEditorPublishFailure(
    responseStatus?: number | null
): SkillEditorPublishFailureCertainty {
    return responseStatus !== undefined &&
        responseStatus !== null &&
        responseStatus >= 400 &&
        responseStatus < 500 &&
        responseStatus !== 408
        ? "rejected"
        : "status-unknown";
}

const settleCompletedDraftOperations = (
    state: SkillEditorState,
    completedOperations: readonly SkillEditorOperationReceipt[]
): SkillEditorState => {
    const committedState = completedOperations.reduce(
        applySkillEditorOperationReceipt,
        state
    );
    return completedOperations.length === 0
        ? committedState
        : clearSkillEditorReviewRequired(committedState);
};

const reconcileWithAuthoritative = (
    state: SkillEditorState,
    attempt: SkillEditorPublishAttempt,
    authoritative: SkillEditorSource
): SkillEditorState =>
    reconcileSkillEditorSaveFailure(state, authoritative, attempt.operation)
        .state;

const createPublishStatusUnknownOutcome = (
    state: SkillEditorState,
    attempt: SkillEditorPublishAttempt,
    completedOperations: readonly SkillEditorOperationReceipt[],
    error: unknown,
    observedPublicationToken: SkillEditorPublicationToken | null
): Extract<SkillEditorPublicationOutcome, { kind: "status-unknown" }> => ({
    kind: "status-unknown",
    state,
    recovery: {
        kind: "publish-status-unknown",
        attempt,
        completedOperations: [...completedOperations],
        error,
        observedPublicationToken,
    },
});

export function reconcileSkillEditorUnknownPublication(
    committedState: SkillEditorState,
    recovery: SkillEditorPublishStatusUnknownRecovery,
    authoritative: SkillEditorSource
): SkillEditorPublicationOutcome {
    const reconciledState = reconcileWithAuthoritative(
        committedState,
        recovery.attempt,
        authoritative
    );
    const observedPublicationToken =
        getSkillEditorPublicationToken(authoritative);
    const publicationMoved = !skillEditorPublicationTokensEqual(
        recovery.attempt.publicationToken,
        observedPublicationToken
    );
    if (publicationMoved && observedPublicationToken.publishedAt !== null) {
        return {
            kind: "applied",
            state: clearSkillEditorReviewRequired(reconciledState),
            completedOperations: [...recovery.completedOperations],
        };
    }
    // A moved token with nothing published is decided, not unknown: the skill is
    // demonstrably unpublished now, and a retry is refused because the tokens
    // differ, so leaving it unknown strands the buffer behind blocked saves.
    if (publicationMoved) {
        return {
            kind: "unpublished-elsewhere",
            state: reconciledState,
            recovery: {
                kind: "publish-unpublished-elsewhere",
                attempt: recovery.attempt,
                completedOperations: [...recovery.completedOperations],
                error: recovery.error,
                observedPublicationToken,
            },
        };
    }
    return createPublishStatusUnknownOutcome(
        reconciledState,
        recovery.attempt,
        recovery.completedOperations,
        recovery.error,
        observedPublicationToken
    );
}

export const evaluateSettledSkillEditorPublishFailure = ({
    state,
    attempt,
    completedOperations,
    error,
    responseStatus,
    authoritative,
}: SkillEditorPublishFailureInput): SkillEditorPublicationOutcome => {
    if (classifySkillEditorPublishFailure(responseStatus) === "rejected") {
        if (completedOperations.length === 0) {
            return {
                kind: "rejected",
                state,
                error,
            };
        }
        const reconciledState = authoritative
            ? reconcileWithAuthoritative(state, attempt, authoritative)
            : state;
        return {
            kind: "draft-saved-publish-rejected",
            state: reconciledState,
            recovery: {
                kind: "draft-saved-publish-rejected",
                attempt,
                completedOperations: [...completedOperations],
                error,
            },
        };
    }

    const unknown = createPublishStatusUnknownOutcome(
        state,
        attempt,
        completedOperations,
        error,
        null
    );
    return authoritative
        ? reconcileSkillEditorUnknownPublication(
              unknown.state,
              unknown.recovery,
              authoritative
          )
        : unknown;
};

export function evaluateSkillEditorPublishFailure(
    input: SkillEditorPublishFailureInput
): SkillEditorPublicationOutcome {
    return evaluateSettledSkillEditorPublishFailure({
        ...input,
        state: settleCompletedDraftOperations(
            input.state,
            input.completedOperations
        ),
    });
}

// The single eligibility test for a publish-only retry. The editor gates its
// retry affordance on this, so a caller that respects it always reaches a real
// publish request rather than a silent no-op.
export function canRetrySkillEditorPublishOnly(
    recovery: SkillEditorPublishStatusUnknownRecovery
): boolean {
    const observedPublicationToken = recovery.observedPublicationToken;
    return (
        observedPublicationToken !== null &&
        skillEditorPublicationTokensEqual(
            recovery.attempt.publicationToken,
            observedPublicationToken
        )
    );
}

export function planSkillEditorPublishOnlyRetry(
    recovery: SkillEditorPublishStatusUnknownRecovery,
    acknowledgment: SkillEditorPublishRetryWarningAcknowledgment
): SkillEditorPublishOnlyRetryPlan | null {
    const observedPublicationToken = recovery.observedPublicationToken;
    if (
        !acknowledgment.acceptsDuplicateVersionRisk ||
        observedPublicationToken === null ||
        !canRetrySkillEditorPublishOnly(recovery)
    ) {
        return null;
    }
    return {
        attempt: {
            operation: { ...recovery.attempt.operation },
            publicationToken: { ...observedPublicationToken },
        },
    };
}
